import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Eye } from "lucide-react";
import { buildPathWithCurrentTenant } from "@/lib/tenantPath";

/**
 * HouseholdDetailsCard - displays household relationship information
 * 
 * Styled to exactly match the Prospect Details card styling.
 * Only renders when a lead/client has household members.
 * 
 * Props:
 * - householdData: Object containing { primary_lead, members, is_primary, household_id }
 * - currentLeadId: The ID of the current lead being viewed
 * - linkPrefix: The URL prefix for navigation (e.g., "/app/leads" or "/app/clients")
 */
export default function HouseholdDetailsCard({ 
  householdData, 
  currentLeadId,
  linkPrefix = "/app/leads"
}) {
  const navigate = useNavigate();
  
  // Button styling to match Edit (pencil) button on Prospect Details card
  const viewButtonClass =
    "h-8 w-8 rounded-full bg-indigo-500/20 p-0 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30";
  
  // Don't render if no household data or no household members
  if (!householdData) return null;
  
  const { primary_lead, members, is_primary, household_id } = householdData;
  
  // Only render if there are household relationships to show
  const hasMembers = members && members.length > 0;
  const hasPrimaryToShow = primary_lead && !is_primary;
  
  if (!hasMembers && !hasPrimaryToShow) return null;
  
  // Combine primary lead info for display when viewing a member
  const primaryDisplay = is_primary ? null : primary_lead;

  return (
    <Card className="glass" data-testid="household-details-card">
      <CardContent className="p-0">
        <div className="flex items-center gap-3 px-4 py-3">
          <Users className="h-5 w-5 text-slate-200/70" />
          <div className="text-lg font-semibold text-slate-100" data-testid="household-details-title">
            Household Details
          </div>
        </div>
        <div className="border-t border-white/10 px-4 py-4">
          <div className="space-y-4 text-sm" data-testid="household-details-content">
            {/* Show primary lead info when viewing a household member */}
            {primaryDisplay && (
              <div data-testid="household-primary-section">
                <div className="text-xs text-slate-200/70 mb-1">Primary Lead</div>
                <div 
                  className="rounded-lg bg-white/5 px-3 py-2"
                  data-testid="household-primary-lead-row"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-slate-100 font-medium">{primaryDisplay.full_name || "—"}</div>
                      {(primaryDisplay.phone || primaryDisplay.phone_number) && (
                        <div className="text-xs text-slate-200/60 mt-0.5">
                          {primaryDisplay.phone || primaryDisplay.phone_number}
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      className={viewButtonClass}
                      onClick={() => navigate(buildPathWithCurrentTenant(`${linkPrefix}/${primaryDisplay.id}`))}
                      data-testid="household-primary-lead-view-button"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Show household members */}
            {hasMembers && (
              <div data-testid="household-members-section">
                <div className="text-xs text-slate-200/70 mb-1">
                  {is_primary ? "Household Members" : "Other Household Members"}
                </div>
                <div className="space-y-2">
                  {members.map((member) => {
                    const isCurrent = member.id === currentLeadId;
                    return (
                      <div
                        key={member.id}
                        className={`rounded-lg bg-white/5 px-3 py-2 ${
                          isCurrent ? "ring-1 ring-indigo-400/30 bg-indigo-500/10" : ""
                        }`}
                        data-testid={`household-member-${member.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-100 font-medium">{member.full_name || "—"}</span>
                              {isCurrent && (
                                <span className="text-xs text-indigo-300/80 bg-indigo-500/20 px-2 py-0.5 rounded-full">
                                  Current
                                </span>
                              )}
                            </div>
                            {(member.phone || member.phone_number) && (
                              <div className="text-xs text-slate-200/60 mt-0.5">
                                {member.phone || member.phone_number}
                              </div>
                            )}
                          </div>
                          {!isCurrent && (
                            <Button
                              type="button"
                              variant="secondary"
                              className={viewButtonClass}
                              onClick={() => navigate(buildPathWithCurrentTenant(`${linkPrefix}/${member.id}`))}
                              data-testid={`household-member-view-button-${member.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
